import { B2B_PROFS_PATH } from '../constants';
import { B2B_IMAGES as img } from '../images';
import type { B2BPageConfig } from '../types';
import {
    B2B_FOOTER_LEGAL_LINKS,
    B2B_FOOTER_LINK_GROUPS,
    B2B_NAV_LINKS,
    B2B_NAV_TAGLINE,
} from './shared.config';

/**
 * Content for the B2B professionals page (served at `/professionals` on the
 * business subdomain; Figma node 492:29954, "B2B Profs"). Same template as
 * brands — only copy and the hero photo differ.
 * Copy is adapted for the professionals audience (architects, designers).
 */

export const profsConfig: B2BPageConfig = {
    routePath: B2B_PROFS_PATH,
    meta: {
        title: 'Become a Pro',
        description:
            'Grow your practice with ArchiPro. Reach the homeowners and clients shaping the projects you want to win.',
    },

    assets: {
        wordmarkWhite: img.wordmarkWhite,
        wordmarkDark: img.wordmarkDark,
        heroBackground: img.profHero,
        statementImage: img.statement,
        marketBackground: img.marketHeadline,
        statsBackground: img.statsBg,
        partnersImage: img.partners,
        audienceMedia: img.clientTestimonialsVideo,
        audienceMediaWebm: img.clientTestimonialsVideoWebm,
        personaHomeowners: img.profPersonaHomeowners,
        personaTrade: img.profPersonaSpecifiers,
        taggingMedia: img.taggingMedia,
        taggingProduct: img.taggingProduct,
        profileMockupBg: img.profileMockupBg,
        profileMockupHero: img.profileMockupHero,
        pillarsImage: img.profPillarsMockup,
        techImage: img.techContent,
        mediaProgramHero: img.mediaProgramVideo,
        mediaProgramHeroWebm: img.mediaProgramVideoWebm,
        filmCover: img.filmCover,
        testimonialImage: img.testimonial,
        testimonialDavidPonting: img.testimonialDavidPonting,
        testimonialJulianGuthrie: img.testimonialJulianGuthrie,
        testimonialHamishMuir: img.testimonialHamishMuir,
        testimonialNicoleTrinity: img.testimonialNicoleTrinity,
        testimonialJosephMammone: img.testimonialJosephMammone,
        testimonialChristianAnderson: img.testimonialChristianAnderson,
        testimonialDavidProject: img.testimonialDavidProject,
        testimonialJulianProject: img.testimonialJulianProject,
        testimonialHamishProject: img.testimonialHamishProject,
        testimonialNicoleProject: img.testimonialNicoleProject,
        testimonialChristianProject: img.testimonialChristianProject,
        leadModalImage: img.leadModalImage,
        leadModalLogomark: img.wordmarkModalLogomark,
        leadFormBackground: img.leadFormBg,
        heroLogomark: img.heroLogomark,
        partnersStoneHouse: img.partnersStoneHouse,
        testimonialThumb: img.testimonialThumb,
        profileCard: img.profileCard,
    },

    topNav: {
        tagline: B2B_NAV_TAGLINE,
        links: B2B_NAV_LINKS,
        wordmarkAssetKey: 'wordmarkWhite',
        primaryCta: { label: 'Apply to Join', opensModal: true },
    },

    hero: {
        backgroundAssetKey: 'heroBackground',
        headline: 'Publish your work to an audience that will appreciate it.',
        scrollCueLabel: 'Scroll',
        logomarkAssetKey: 'heroLogomark',
    },

    sections: [
        {
            type: 'statement-band',
            text: 'We believe architecture should feel more accessible, more connected and more culturally significant.',
            mediaAssetKey: 'statementImage',
        },
        {
            type: 'intro-band',
            eyebrow: '(About)',
            bodyLead:
                'ArchiPro is where architecture and design gets discovered and decided ',
            bodyTail:
                '— connecting professionals, homeowners and the brands they specify, in one platform.',
            frameHeight: 572,
            mobileFrameHeight: 353,
            bodyIndentPx: 295,
            ctaLabel: 'Apply to Join',
            ctaOpensModal: true,
            cornerArrow: true,
            cornerArrowHeightPx: 46.57,
            cornerArrowTopPx: 7.72,
        },
        {
            type: 'headline',
            eyebrow: '(About)',
            headline:
                'Built in New Zealand. Proven in Australia,\nComing to America.',
            backgroundAssetKey: 'marketBackground',
        },
        {
            type: 'intro-band',
            eyebrow: '(Invite-only platform)',
            bodyLead:
                'Some of America’s most considered firms are already here. ',
            bodyTail: 'Now we’re carefully curating the brands around them.',
            frameHeight: 316,
            mobileCtaLabel: 'Apply to Join',
            bodyIndentPx: 295,
            cornerArrow: true,
            cornerArrowHeightPx: 42.63,
            cornerArrowTopPx: 7.08,
        },
        {
            type: 'partners',
            eyebrow: '(Invite-only platform)',
            title: 'Meet our professionals',
            imageAssetKey: 'partnersImage',
            activeIndex: 2,
            visibleCount: 6,
            // Each partner shows the real profile banner of the professional at
            // `professionalUrlSegment` (fetched by the route loader); the name
            // links to that professional's profile on the main consumer site.
            // Partners whose banner can't be resolved are omitted from the strip.
            partners: [
                {
                    name: 'CLB Architects',
                    professionalUrlSegment: 'clb-architects',
                },
                {
                    name: 'McLeod Bovell Modern Houses',
                    professionalUrlSegment: 'mcleod-bovell-modern-houses',
                },
                {
                    name: 'Andersson-Wise',
                    professionalUrlSegment: 'andersson-wise-architects',
                },
                {
                    name: 'FINNE Architects',
                    professionalUrlSegment: 'finne-architects',
                },
                {
                    name: 'Prentiss + Balance + Wickline',
                    professionalUrlSegment:
                        'prentiss-balance-wickline-architects',
                },
                { name: 'Luca Studio', professionalUrlSegment: 'luca-studio' },
                {
                    name: 'Furman + Keil Architects',
                    professionalUrlSegment: 'furman-keil-architects',
                },
            ],
        },
        {
            type: 'accordion',
            eyebrow: '(Platform)',
            mobileEyebrow: '(Invite only Platform)',
            title: 'ArchiPro Pillars: Projects, Products and Professionals',
            defaultOpenId: 'projects',
            imageAssetKey: 'pillarsImage',
            ctaLabel: 'Apply to Join',
            items: [
                {
                    id: 'projects',
                    title: 'Projects',
                    body: 'Publish your projects to a national audience actively researching their next build.',
                },
                {
                    id: 'professionals',
                    title: 'Professionals',
                    body: 'A profile that presents your practice and connects you with high-intent clients.',
                },
                {
                    id: 'products',
                    title: 'Products',
                    body: 'Discover and specify the products that bring your projects to life.',
                },
            ],
        },
        {
            // Stats statement (Figma `443:24330`, 1920×1281): photo-bg headline
            // (denim-jacket interior shot), 80px Neue Haas Roman two-line
            // headline + 42.911px subheadline, both centred. Headline sits
            // higher than the Market headline — at `calc(50% − 8.22vw)`.
            type: 'headline',
            headline:
                "Of Australia's top 10% wealthiest households, one in three uses ArchiPro every year.",
            subheadline:
                "We're building the same audience in the United States.",
            backgroundAssetKey: 'statsBackground',
            headlineWidthPercent: 89.83,
            headlineTopOffsetVw: -8.219,
            subheadlineWidthPercent: 53.19,
            subheadlineTopOffsetVw: 2.998,
            subheadlineSizeVw: 2.235,
        },
        {
            type: 'intro-band',
            eyebrow: '(Meet our Audience)',
            bodyLead:
                'From architects and designers to homeowners and builders, ArchiPro connects brands with key stakeholders ',
            bodyTail: 'shaping projects.',
            frameHeight: 351,
            mobileCtaLabel: 'Apply to Join',
            eyebrowPlacement: 'left-edge',
            bodyLeftPx: 390,
            bodyWidthPx: 1518,
            bodyTopPx: 7,
            bodyIndentPx: 295,
        },
        {
            type: 'audience',
            personas: [
                {
                    name: 'Design-conscious homeowners: They influence what gets specified.',
                    description:
                        'People researching professionals, exploring projects and discovering products for a build or renovation. They’re spending time with architecture and interiors to help shape ideas and make decisions about their own home.',
                    imageAssetKey: 'personaHomeowners',
                },
                {
                    name: 'Specifiers: They decide what gets built.',
                    description:
                        'Architects, interior designers, and construction professionals researching products for active projects. They compare specifications, save products to project boards, and make the choices that determine what gets built. ArchiPro is where your industry works — not just where homeowners find you.',
                    imageAssetKey: 'personaTrade',
                },
            ],
            ctaLabel: 'Apply to Join',
        },
        {
            type: 'media-band',
            assetKey: 'audienceMedia',
            webmAssetKey: 'audienceMediaWebm',
            isVideo: true,
        },
        {
            type: 'profile-showcase',
            backgroundAssetKey: 'profileMockupBg',
            eyebrow: '(Platform)',
            title: 'What your brand looks like on ArchiPro.  And what powers it.',
            intro: 'Your ArchiPro profile is a permanent, public presence for your firm — not a listing, but a living portfolio that grows with your body of work.',
            cardImageAssetKey: 'profileCard',
            cardLogomarkAssetKey: 'heroLogomark',
            menuItems: [
                {
                    label: 'Your Profile',
                    active: true,
                    intro: 'Your ArchiPro profile is a permanent, public presence for your firm — not a listing, but a living portfolio that grows with your body of work.',
                },
                {
                    label: 'Your Projects',
                    intro: 'Published with professional imagery, tagged with the products and collaborators involved. Every project is discoverable by type and location. Each one a new path to your firm.',
                },
                {
                    label: 'Your Network',
                    intro: 'Every project you publish brings together products and the professionals who helped build it. Tag them, and your firm surfaces on their brand pages, their profiles and in front of the people researching them. The more you tag, the more places your work appears — every product, every collaborator, another route back to your firm.',
                },
                {
                    label: 'Your Story',
                    intro: 'Your company philosophy, credentials, awards, testimonials and team. The context that turns a project search into a conversation.',
                },
            ],
            brandName: 'Andersson-Wise Architects',
            brandRole: 'Landscape Architect',
            cardWordmark: 'ArchiPro',
            cardNavLabels: ['Projects', 'Products', 'Professionals', 'About'],
            cardHeroAssetKey: 'profileMockupHero',
            cardCaption: 'Te Arai — North Auckland',
            cardMeta: ['Auckland, Melbourne', 'pattersons.co.nz'],
            cardBody:
                'There are places in Aotearoa where architecture must tread lightly. At Te Arai, north of Auckland, the coastline still carries the rawness of a landscape that hasn’t been overly handled. Its wind-shaped dunes, pines leaning toward the sea and long horizons without habitation are carefully integrated with the immaculate green ribbon of fairway curling through.',
        },
        {
            type: 'feature',
            eyebrow: '(Discover)',
            title: 'ArchiPro enables practices to showcase projects within the context of ',
            titleTail:
                'architecture and interiors discovered by clients across the country. ',
            body: 'Projects published on ArchiPro reach an engaged national audience, compounding your visibility over time. Each new tag is another path back to your practice — from project galleries to product specification trails.',
            ctaLabel: 'Apply to Join',
            ctaOpensModal: true,
            mediaAssetKey: 'taggingMedia',
            productCard: {
                productImageAssetKey: 'taggingProduct',
                price: 'FROM $1,599',
                productName: 'Lobby Chair',
                brandName: 'Unison Workplaces',
                // Figma 478:24502 — tag dot just left of the card, on the chair.
                dotLeftPct: 33.5,
                dotTopPct: 72.4,
            },
            frameHeight: 1318,
        },
        {
            type: 'accordion',
            eyebrow: '(ArchiPro Business Manager)',
            title: 'The ArchiPro tech powering your profile presence',
            defaultOpenId: 'content',
            imageAssetKey: 'techImage',
            frameHeight: 1311,
            titleWidthPx: 990,
            titleIndentPx: 380,
            items: [
                {
                    id: 'content',
                    title: 'Content',
                    body: "Control what appears and how it's presented. AI-powered tools help you build project pages without needing a copywriter or a marketing team. Manage your portfolio, publish articles and add case studies — all from one place.\n\nEvery page, project and image is indexed on Google, with customizable meta tags, descriptions and keywords to attract your ideal clients. ArchiPro's high domain authority strengthens your firm's visibility in organic search, and every profile creates valuable backlinks to your own website. The more you publish, the greater your reach.",
                    // TODO: temp images — replace with final per-item art.
                    imageAssetKey: 'techImage',
                },
                {
                    id: 'analytics',
                    title: 'Analytics',
                    body: "See who's engaging with your firm — by project type, location and demographics. Track how they move through your profile and what they look at. Understand which projects drive the most interest and where your enquiries are coming from.",
                    imageAssetKey: 'profileMockupHero',
                },
                {
                    id: 'archipro-pixel',
                    title: 'ArchiPro Pixel',
                    body: 'If a user arrives at your website via ArchiPro, our Pixel tags them so you can see everything they do next — that first visit and every return after. We provide a small piece of code for your website that tracks what our traffic does once it lands: which sources they come back through, what pages they look at, and what actions they take. The full picture, not just what happens on the ArchiPro platform.',
                    imageAssetKey: 'statementImage',
                },
            ],
        },
        {
            type: 'intro-band',
            eyebrow: '(ArchiPro Media Program)',
            bodyLead:
                'ArchiPro combines platform presence with editorial storytelling, films and publishing ',
            bodyTail:
                'designed to connect brands with a highly curated design audience.',
            frameHeight: 416,
            bodyIndentPx: 395,
            eyebrowTrackingEm: -0.048,
            cornerArrow: true,
            cornerArrowHeightPx: 51.96,
            cornerArrowTopPx: 8.65,
        },
        {
            type: 'media-program',
            heroAssetKey: 'mediaProgramHero',
            heroWebmAssetKey: 'mediaProgramHeroWebm',
            heroIsVideo: true,
            heroLink: 'https://www.youtube.com/watch?v=Ym_zSlqZpNI',
            body: "ArchiPro publishes across its own platform, email, social, YouTube and digital magazines. In Australia and New Zealand, we reach millions of design-engaged people each month. We're building the same in the United States. Your profile is your foundation. The media program is how you amplify it.",
            cards: [
                {
                    title: 'Editorial',
                    body: 'Design interviews, project stories, industry news and product deep-dives - written for people who care how things get built.',
                },
                {
                    title: 'Films',
                    body: "Architectural films, studio visits and product stories. Not how-to content. The kind of thing you'd actually watch.",
                },
                {
                    title: 'Digital Publications',
                    body: 'Distinct mastheads for homeowners and professionals - bringing together editorial, projects, products and people stories.',
                },
                {
                    title: 'Social',
                    body: 'An owned program across Instagram, Facebook, Pinterest and Youtube that reaches audiences beyond ArchiPro itself. Your brand travels with it.',
                },
                {
                    title: 'Events',
                    body: 'National design events held in major cities. Homeowners actively planning a build, alongside industry professionals - discovering products and making project decisions in person.',
                },
                {
                    title: 'Dinners and showroom events',
                    body: 'Intimate, invitation-only gatherings hosted in showrooms and private venues. Architects, designers and specifiers - built around a launch, a collection, or a conversation worth having',
                },
            ],
            filmCoverAssetKey: 'filmCover',
        },
        {
            type: 'intro-band',
            eyebrow: '(Success Stories)',
            bodyLead: 'Trusted by architects and designers.  ',
            bodyTail:
                'An ecosystem where products, projects, specification and discovery naturally intersect.',
            frameHeight: 330,
            mobileCtaLabel: 'Apply to Join',
            bodyIndentPx: 315,
            eyebrowTrackingEm: -0.048,
            tailColor: '#cfcfcf',
            cornerArrow: true,
            cornerArrowHeightPx: 46.5,
            cornerArrowTopPx: 7.76,
        },
        {
            type: 'testimonial',
            viewProjectLabel: '(View Project)',
            testimonials: [
                {
                    quote: "Architecture has the power to delight, however no-one can sell a secret - no matter how beautiful. Archipro solves that issue, extending our horizon, and connecting us with engaging clients that dare to dream. I'm proud of our long association with Milot, and the growth we will all enjoy through the global launch of his premium product platform.",
                    attributionName: 'David Ponting',
                    attributionRole: 'Ponting Fitzgerald Architects',
                    imageAssetKey: 'testimonialImage',
                    projectName: 'Featured Project',
                    projectRole: 'Ponting Fitzgerald Architects',
                    projectThumbnailAssetKey: 'testimonialDavidProject',
                    viewProjectHref: '#',
                },
                {
                    quote: 'We met Milot from Archipro when 2014 it was a newly minted company. His enthusiasm and vision were infectious, and we were keen to sign up and see what this new marketing platform could do for our young architectural practice. Over the years, Archipro has evolved and expanded exponentially into the brilliant platform it now offers, bringing potential clients to designers and suppliers across the construction industry. We continue to get comments from new client enquiries who tell us about our projects they have viewed on Archipro.',
                    attributionName: 'Julian Guthrie',
                    attributionRole: 'Julian Guthrie Architects',
                    imageAssetKey: 'testimonialJulianGuthrie',
                    projectName: 'Featured Project',
                    projectRole: 'Julian Guthrie Architects',
                    projectThumbnailAssetKey: 'testimonialJulianProject',
                    viewProjectHref: '#',
                },
                {
                    quote: 'Mason&Wales are proud to have been one of the first architecture practices to join ArchiPro, and greatly value our ongoing relationship with the platform and the people behind it. It is a proven and effective tool to showcase our completed Projects for us externally, while also serving our clients internally as a tool to find new products. ArchiPro has proven international reach, connecting us with pre-qualified clients who have engaged Mason&Wales as Architects for projects around New Zealand, Australia and further afield.',
                    attributionName: 'Hamish Muir',
                    attributionRole: 'Mason And Wales Architects',
                    imageAssetKey: 'testimonialHamishMuir',
                    projectName: 'Featured Project',
                    projectRole: 'Mason And Wales Architects',
                    projectThumbnailAssetKey: 'testimonialHamishProject',
                    viewProjectHref: '#',
                },
                {
                    quote: 'Becoming one of the first interior designers to join the Archipro platform over 10 years ago was a smart decision. Archipro took the time to understand our business, our story and our aspirations and worked with us to develop articles and marketing plans to showcase and articulate these meaningfully to a wide audience. Archipro has been instrumental in providing us that visibility which has allowed us to form strong and collaborative relationships with like minded industry professionals. We value their fresh approach and constant strive for better. No one does it better.',
                    attributionName: 'Nicole Trinity',
                    attributionRole: 'Trinity Interior Design',
                    imageAssetKey: 'testimonialNicoleTrinity',
                    projectName: 'Featured Project',
                    projectRole: 'Trinity Interior Design',
                    projectThumbnailAssetKey: 'testimonialNicoleProject',
                    viewProjectHref: '#',
                },
                {
                    quote: "ArchiPro has been a valuable platform for our practice, not only providing a space to showcase our work but also advocating for architects and the importance of the architectural experience. Through ArchiPro, we've been able to build and maintain a strong professional presence, positioning ourselves as a trustworthy and skilled team to clients actively seeking design expertise. The platform also keeps us connected to the latest materials, products, and industry trends, helping us stay relevant and informed while ensuring our projects receive high visibility and recognition.",
                    attributionName: 'Joseph Mammone',
                    attributionRole: 'Joseph Mammone Architecture',
                    imageAssetKey: 'testimonialJosephMammone',
                    // Joseph's Figma variant has no project thumbnail.
                },
                {
                    quote: 'We have found the Archipro Platform can lead to broader exposure of our finished projects, and has resulted in some interesting new Project client collaborations.',
                    attributionName: 'Christian Anderson',
                    attributionRole: 'Christian Anderson Architects',
                    imageAssetKey: 'testimonialChristianAnderson',
                    projectName: 'Featured Project',
                    projectRole: 'Christian Anderson Architects',
                    projectThumbnailAssetKey: 'testimonialChristianProject',
                    viewProjectHref: '#',
                },
            ],
        },
    ],

    finalCta: {
        eyebrow: '(Apply to Join)',
        headline: 'Apply to Join',
        body: "Request a conversation and we'll show you what the platform looks like from the inside.",
    },

    leadForm: {
        heading: 'Apply to Join',
        submitLabel: 'Request a Conversation',
        successHeading: "Thanks — we'll be in touch.",
        successBody:
            'Our team will review your details and reach out shortly to start the conversation.',
        modalImageAssetKey: 'leadModalImage',
        modalBlurb:
            "ArchiPro works best with practices that value design, think long-term and care about how their work is presented. If that sounds like you, let's talk.",
    },

    footer: {
        subscribeHeading: 'Subscribe',
        subscribeBlurb:
            'Stay informed about the newest projects, products, and technologies shaping the future of the architecture and design environment.',
        subscribeLabel: 'Subscribe',
        linkGroups: B2B_FOOTER_LINK_GROUPS,
        legalLinks: B2B_FOOTER_LEGAL_LINKS,
        wordmarkAssetKey: 'wordmarkDark',
        copyright: '© ArchiPro 2024',
    },
};
